import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchScoreboard } from "../api";
import { useScoreboardPoll } from "../hooks/useScoreboardPoll";
import ScoreboardFrame from "../components/scoreboard/ScoreboardFrame";
import ScoreboardContent from "../components/scoreboard/ScoreboardContent";
import ScoreboardErrorBoundary from "../components/scoreboard/ScoreboardErrorBoundary";
import SponsorStrip from "../components/scoreboard/SponsorStrip";
import QRCorner from "../components/scoreboard/QRCorner";
import { sponsorVariantForModeType, isQrCapableModeType } from "../components/scoreboard/modeTypes";
import { VenueTimeZoneProvider } from "../components/scoreboard/VenueTimeZoneContext";
import { classifyBoardRenderState, BOARD_RENDER_REASON } from "../components/scoreboard/boardRenderState";
import { SCOREBOARD_STATUS, postScoreboardStatus } from "../components/scoreboard/boardStatusChannel";

// #1907 AC9 — 1000ms, down from 5000. See useScoreboardPoll.js's header for
// the full justification against the <=2s scoreboard freshness budget
// (docs/score/query-performance.md).
const POLL_INTERVAL_MS = 1000;

// #1866 — pure QR-URL builder, exported for unit testing (mirrors
// ClassResultsPage's groupResultsByDivision pattern: a real call site's own
// logic, not a reimplementation of it). A board addressed by usefCompId
// (#1703 short form) has a raw route `showId` param that is NOT the show's
// real entity id — resolveClassByNumber/getPublicClassResults (the QR
// target's two lookups) do a bare GetCommand by entity id with no
// usefCompId fallback, so a QR built from the raw param is a dead link.
// score-api's getPublicScoreboard now returns the show's OWN resolved
// entity id as `data.showId` (#1866) for exactly this reason — prefer it
// over the raw route param, same "prefer the resolved server value" shape
// already used for `yearForQr` below. Falls back to the raw route param
// only when the server hasn't returned one yet (no poll has landed).
export function buildScoreboardQrUrl({ origin, routeShowId, data, routeYear }) {
  if (!isQrCapableModeType(data?.mode?.modeType)) return null;
  const classNumberForQr = data?.fields?.classNumberName?.classNumber ?? null;
  if (!classNumberForQr) return null;
  const yearForQr = data?.year ?? routeYear;
  if (!yearForQr) return null;
  const showIdForQr = data?.showId ?? routeShowId;
  return `${origin}/results/${encodeURIComponent(showIdForQr)}/${encodeURIComponent(yearForQr)}/${encodeURIComponent(classNumberForQr)}`;
}

// #1637 — the public scoreboard route (design §4.1): `/scoreboard/:showId/
// :year` (default view) and `/scoreboard/:showId/:year/:slug` (named view).
// #1703 added two short forms sharing these SAME route shapes: `/scoreboard/
// :showId/:slug` (showId = usefCompId, year resolved server-side, AC2) and
// `/scoreboard/:showId/:year/:slug` with showId = usefCompId (AC1, no
// routing change needed — the 3-segment form was never ambiguous).
//
// The AC3 disambiguation rule (App.jsx's route comment: is the 2nd segment
// a bare 4-digit year or a slug?) lives ENTIRELY in score-api's
// getPublicScoreboard (isBareFourDigitYear, scoreboard-public.js) and
// nowhere on this page, deliberately: `year` below is forwarded to
// fetchScoreboard verbatim under its OWN raw route-param name regardless of
// which reading applies, and api.js's fetchScoreboard builds the request
// path by positionally concatenating whatever non-null segments it's given
// (`[showId, year, slug].filter(...)`) — so a slug value sitting in the
// `year` variable produces the exact same request URL a `slug` variable
// would. Duplicating the AC3 predicate here would decide nothing this page
// actually does; the one place it changes behavior is the server route.
// Full-bleed, chrome-free (App.jsx hides BuildBadge/UsaBadge here), themed
// by ScoreboardFrame, live-updated by useScoreboardPoll every 1s (#1907
// AC9). A failed
// poll never clears the last good payload (design §4.2) — the board simply
// keeps rendering what it already has.
export default function ScoreboardPage() {
  const { showId, year, slug } = useParams();

  const fetchPayload = useCallback(
    () => fetchScoreboard({ showId, year, slug }),
    [showId, year, slug]
  );

  const { data, error, lastUpdated } = useScoreboardPoll(fetchPayload, {
    intervalMs: POLL_INTERVAL_MS,
  });

  // True only before the FIRST successful fetch ever lands — once `data`
  // exists, a later failed poll must never blank it back to this state.
  const isLoading = !data && !lastUpdated;

  // #1798 AC8 — fed by BOTH boundaries below (the outer whole-frame one and
  // ScoreboardFrame's own nested content-only one), whichever catches a
  // throw first. `true` on a caught render exception (componentDidCatch),
  // `false` on a clean mount AND on the boundary's own resetKey-driven
  // recovery (componentDidUpdate) — see ScoreboardErrorBoundary's own
  // header for why both call sites are needed to keep this flag accurate
  // across a poll that fixes a previously-throwing payload.
  const [renderErrorFlag, setRenderErrorFlag] = useState(false);

  // #1639 (design §7.1) — the FE owns the "only sponsor-capable types honor
  // sponsorId" gate: the composed endpoint resolves `sponsor` regardless of
  // modeType (scoreboard-public.js's resolveSponsorEnvelope). A modeType this
  // map doesn't recognize as sponsor-capable gets sponsorVariant === null, so
  // no sponsorSlot is even passed — SponsorStrip itself ALSO no-ops on a
  // missing sponsor/image, so this is belt-and-braces, not the only gate.
  const sponsorVariant = sponsorVariantForModeType(data?.mode?.modeType);
  const sponsorSlot = sponsorVariant ? (
    <SponsorStrip sponsor={data?.sponsor} variant={sponsorVariant} />
  ) : null;

  // #1641 (design §6.2/§6.3) — the QR corner encodes the public class-
  // results page for whatever class this board is currently showing.
  // isQrCapableModeType is the FE-owned gate (mirrors
  // sponsorVariantForModeType's same fails-closed shape above); after #1794
  // pared the mode-type set down, it was true for exactly the two types
  // whose composer (score-api's scoreboard-mode-payload.js) always includes
  // `fields.classNumberName` — POST_RIDE_SCORE and END_OF_CLASS_RESULT.
  // Card #2283 added RIDE_INTRO_DETAILED as a third: its own composer
  // (composeRideIntroDetailed) always sets classNumberName too, and #2283
  // overrules the old §6.2 item 3 exclusion (stale, per p4.html and #2283's
  // own AC2 — see modeTypes.js's QR_CAPABLE_MODE_TYPES comment).
  //
  // #1854 (split off #1790) — the QR now encodes a short, human-readable
  // class NUMBER (`fields.classNumberName.classNumber`), not the internal
  // fvd: classId `data.displayClassId`/`payload.classId`/
  // `payload.currentClassId` used to carry. This reads from the SAME
  // classItem the board's OWN currently-displayed data was composed from
  // (classNumberNameOf(classItem) in composePostRideScore/
  // composeEndOfClassResult) — so it inherits #1790's displayClassId fix
  // for free: whatever class the board is actually showing is the class
  // this number names, never a ring-following class the display has
  // already moved past.
  // Deliberate decision (#1854): a class with a className but no printed
  // classNumber now renders NO QR at all (classNumberForQr stays null, the
  // qrSlot check below short-circuits) rather than a QR that can never
  // resolve. Before this card the QR always encoded the internal fvd:
  // classId, which is always present, so a QR always rendered even for a
  // classless-number class. Failing closed here is intentional: a scannable
  // but permanently-404ing QR is worse than no QR, and score-api's new
  // resolveClassByNumber has nothing to resolve without a classNumber to
  // look up.
  // #1703 — the resolved show year comes from the API response (`data.year`,
  // always present once a payload has loaded), never the raw URL param: the
  // short slug-only form carries no :year segment at all, so `year` here
  // may be undefined even though the server already knows the real one.
  // #1866 — same reasoning now also covers `showId`: see
  // buildScoreboardQrUrl's own header above for why the raw route param
  // can't be trusted either.
  const qrUrl = buildScoreboardQrUrl({ origin: window.location.origin, routeShowId: showId, data, routeYear: year });
  const qrSlot = qrUrl ? <QRCorner url={qrUrl} /> : null;

  // #1788 — wraps the WHOLE frame, not just the content, because the venue
  // clock and the "last update" footer live in the frame's own slots.
  // `data.venueTimezone` arrives already resolved from score-api; before the
  // first poll lands it is undefined and the provider falls back to Pacific,
  // which is what this board rendered for every show before #1788.
  //
  // #1798 AC2 — TWO boundaries, deliberately nested, not one:
  //   - This outer one wraps the WHOLE frame, because SponsorStrip/QRCorner
  //     above are also built straight from this same poll's `data` and
  //     rendered as ScoreboardFrame's siblings-of-content, so a thrown
  //     exception in either is just as much "the board subtree" as one in
  //     ScoreboardContent, and neither is inside the inner boundary below.
  //   - ScoreboardFrame.jsx nests a SECOND boundary around its own content
  //     slot (`resetKey`/`logoAssetUrl` passed straight through here), so a
  //     throw confined to ScoreboardContent keeps the frame chrome — the
  //     venue clock in particular, the audience's only liveness cue since
  //     #1790 removed the staleness line — instead of blanking the whole
  //     screen for a fault the frame itself had nothing to do with.
  // `resetKey` is the poll's own `lastUpdated` timestamp either way — the
  // next successful poll clears a caught error and gives the board a fresh
  // render attempt, rather than staying on Blank forever after one bad
  // payload.
  const resetKey = lastUpdated ? lastUpdated.getTime() : null;

  // #1798 AC8 — reports this same board's status to whatever embeds it,
  // over postMessage (boardStatusChannel.js). A no-op outside an iframe
  // (postScoreboardStatus checks `window.parent === window`), so this has
  // no effect on the real public board a projector loads directly — only
  // RemotePage's own iframe preview of this exact route receives it. Runs
  // AFTER every poll settles (success or failure) and after a render-error
  // flag flip, so the operator sees a fault the moment this page itself
  // knows about it, not on some separate cadence.
  useEffect(() => {
    if (renderErrorFlag) {
      postScoreboardStatus(SCOREBOARD_STATUS.RENDER_ERROR);
      return;
    }
    if (error) {
      postScoreboardStatus(SCOREBOARD_STATUS.POLL_ERROR, error.message);
      return;
    }
    if (!data) return; // pre-first-load: no status yet, let the 15s timeout own this
    const renderState = classifyBoardRenderState({ mode: data.mode, fields: data.fields });
    if (renderState.reason === BOARD_RENDER_REASON.UNKNOWN_MODE) {
      postScoreboardStatus(SCOREBOARD_STATUS.BLANK_UNKNOWN_MODE);
    } else if (renderState.reason === BOARD_RENDER_REASON.NO_ACTIVE_CLASS) {
      postScoreboardStatus(SCOREBOARD_STATUS.BLANK_NO_ACTIVE_CLASS);
    } else {
      postScoreboardStatus(SCOREBOARD_STATUS.OK);
    }
  }, [renderErrorFlag, error, data]);

  return (
    <ScoreboardErrorBoundary
      resetKey={resetKey}
      logoAssetUrl={data?.mode?.logoAssetUrl}
      theme={data?.theme}
      onRenderStatus={setRenderErrorFlag}
    >
      <VenueTimeZoneProvider timeZone={data?.venueTimezone}>
        <ScoreboardFrame
          theme={data?.theme}
          lastUpdated={lastUpdated}
          isLoading={isLoading}
          sponsorSlot={sponsorSlot}
          qrSlot={qrSlot}
          resetKey={resetKey}
          logoAssetUrl={data?.mode?.logoAssetUrl}
          onRenderStatus={setRenderErrorFlag}
        >
          {data ? (
            <ScoreboardContent mode={data.mode} fields={data.fields} banner={data.banner} theme={data.theme} />
          ) : null}
        </ScoreboardFrame>
      </VenueTimeZoneProvider>
    </ScoreboardErrorBoundary>
  );
}
